import React, { type ReactNode, useCallback } from 'react';

import useCrossProductUrlWrapper from '@atlassiansox/analytics-cross-product-interaction-client/use-cross-product-url-wrapper';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { LinkMenuItem as MenuLinkItemPrimitive } from '@atlaskit/side-nav-items/link-menu-item';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import { useL1ItemDragAndDrop } from '@atlassian/navigation-drag-and-drop';

import { ANALYTICS_CHANNEL } from '../../constants';
import type {
	GlobalShortcutActionsItem,
	GlobalShortcutConfiguration,
	GlobalShortcutLinkItem,
} from '../../types';
import { ElementEventListener } from '../../utils/element-event-listener';
import { useDragAndDropOptions } from '../../utils/use-drag-and-drop-options';
import { ShortcutActionMenu } from '../shortcut-action';

export type MenuLinkItemProps = Omit<
	GlobalShortcutLinkItem,
	'ref' | 'type' | 'apsKey' | 'fallbackUrl' | 'ItemWrapper'
> & {
	ariaLabel?: string;
	isReorderable?: boolean;
	isSelected?: boolean;
	openInSameTab?: boolean;
	optionalRef?: GlobalShortcutConfiguration['ref'];
	product?: string;
};

export type MenuLinkItemDraggableProps = Omit<MenuLinkItemProps, 'menuItemId'> & {
	menuItemId: string;
	url: string;
};

export function MenuLinkItem({
	analyticsName,
	url,
	label,
	labelElem,
	icon,
	ariaLabel,
	actions,
	onClick,
	onRightClick,
	onFocus,
	onMouseEnter,
	optionalRef,
	isReorderable,
	menuItemId,
	isSelected,
	product = 'globalNavigation',
	openInSameTab,
}: MenuLinkItemProps): React.JSX.Element | null {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { enabled: dndEnabled } = useDragAndDropOptions();

	const analyticsUrlWrapper = useCrossProductUrlWrapper({
		bridge: 'globalShortcuts',
		product,
	});

	const getTargetUrl = useCallback(
		(url: string) => {
			// For SPA links, we don't want to wrap the URL
			if (!isValidUrl(url)) {
				return url;
			}

			try {
				const parsedUrl = new URL(url);
				const continueParam = parsedUrl.searchParams.get('continue');
				if (!continueParam) {
					// If there is no continue param, we just wrap the whole URL
					return analyticsUrlWrapper(url);
				}

				// Safely deserialize identity links e.g: https://id.atlassian.com/login?login_hint=...
				const wrappedContinueUrl = analyticsUrlWrapper(continueParam);
				// Reconstruct the original URL with the wrapped continue param
				parsedUrl.searchParams.set('continue', wrappedContinueUrl);
				return parsedUrl.toString();
			} catch {
				// Gracefully handle any URL parsing errors by returning the original URL
				// TODO: Log error to Sentry
				return url;
			}
		},
		[analyticsUrlWrapper],
	);

	const onClickMenuLinkItem = useCallback(
		(event?: React.MouseEvent | React.KeyboardEvent) => {
			fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
			onClick?.(event);
		},
		[createAnalyticsEvent, analyticsName, onClick],
	);

	// If a shortcut doesn't have a URL, don't show it. This is a change from earlier behaviour in which
	// the shortcut would be shown, but greyed-out (disabled).
	if (!url) {
		return null;
	}

	const targetUrl = getTargetUrl(url);

	return (
		<ElementEventListener onRightClick={onRightClick} onMouseEnter={onMouseEnter} onFocus={onFocus}>
			{dndEnabled && menuItemId ? (
				<MenuLinkItemDraggableComponent
					actions={actions}
					ariaLabel={ariaLabel}
					icon={icon}
					isReorderable={isReorderable}
					isSelected={isSelected}
					label={label}
					labelElem={labelElem}
					menuItemId={menuItemId}
					onClick={onClickMenuLinkItem}
					openInSameTab={openInSameTab}
					optionalRef={optionalRef}
					url={targetUrl}
				/>
			) : (
				<MenuLinkItemComponent
					actions={actions}
					ariaLabel={ariaLabel}
					icon={icon}
					isReorderable={isReorderable}
					isSelected={isSelected}
					label={label}
					labelElem={labelElem}
					onClick={onClickMenuLinkItem}
					openInSameTab={openInSameTab}
					optionalRef={optionalRef}
					url={targetUrl}
				/>
			)}
		</ElementEventListener>
	);
}

export function fireMenuLinkItemClickAnalyticsEvent(
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	analyticsName?: string,
): void {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'globalNavigation',
		attributes: {
			name: analyticsName,
		},
	}).fire(ANALYTICS_CHANNEL);
}

/**
 * Utility to return whether actions and options should be shown
 */
function getShouldShowActionsAndOptions(
	actions?: GlobalShortcutActionsItem[],
	optionsMenuElem?: ReactNode,
) {
	return (actions && actions.length >= 0) || !!optionsMenuElem;
}

/**
 * Actions and Options component to render both the action menu and options menu
 * beside each other on hover/focus
 */
function ActionsAndOptions({
	actions,
	analyticsName,
	optionsMenuElem,
}: {
	actions?: GlobalShortcutActionsItem[];
	analyticsName?: string;
	optionsMenuElem?: ReactNode;
}) {
	return (
		<>
			{actions && actions.length && (
				<ShortcutActionMenu actions={actions} analyticsName={analyticsName} />
			)}
			{optionsMenuElem}
		</>
	);
}

function MenuLinkItemComponent({
	url,
	label,
	labelElem,
	icon,
	ariaLabel,
	actions,
	onClick,
	optionalRef,
	isSelected,
	openInSameTab,
	optionsMenuElem,
	analyticsName,
}: MenuLinkItemProps) {
	const mergedRefs = mergeRefs(optionalRef ? [optionalRef] : []);
	const shouldShowActionsAndOptions = getShouldShowActionsAndOptions(actions, optionsMenuElem);

	return (
		<MenuLinkItemPrimitive
			actionsOnHover={
				shouldShowActionsAndOptions && (
					<ActionsAndOptions
						actions={actions}
						analyticsName={analyticsName}
						optionsMenuElem={optionsMenuElem}
					/>
				)
			}
			elemBefore={icon}
			elemAfter={!openInSameTab && <LinkExternalIcon label="" size="small" spacing="spacious" />}
			href={url || ''}
			isSelected={isSelected}
			onClick={onClick}
			ref={mergedRefs}
			target={openInSameTab ? '_self' : '_blank'}
		>
			<span aria-label={ariaLabel}>{labelElem ?? label}</span>
		</MenuLinkItemPrimitive>
	);
}

function MenuLinkItemDraggableComponent({
	url,
	label,
	labelElem,
	icon,
	ariaLabel,
	actions,
	onClick,
	isReorderable,
	optionalRef,
	menuItemId,
	openInSameTab,
	optionsMenuElem,
	analyticsName,
	isSelected,
}: MenuLinkItemDraggableProps) {
	const { enabled } = useDragAndDropOptions();
	const { draggableRef, isDragging, maybeDragPreview, dropTargetRef, dropIndicator } =
		useL1ItemDragAndDrop({
			id: menuItemId,
			isExpandable: false,
			isExpandOrFlyoutOpen: false,
			l1DragAndDropEnabled: enabled,
			onExpandFlyoutToggle: () => {},
			peek: false,
			item: {
				type: 'link',
				elemBefore: icon,
				label,
				labelElem,
				href: url || '',
				isReorderable,
			},
			group: L1_MENU_GROUPS.SHORTCUTS,
		});

	// Can possibly be undefined
	const draggableRefCollection = draggableRef ? [draggableRef] : [];
	const mergedRefs = mergeRefs(
		optionalRef ? [optionalRef, ...draggableRefCollection] : [...draggableRefCollection],
	);
	const shouldShowActionsAndOptions = getShouldShowActionsAndOptions(actions, optionsMenuElem);

	return (
		<>
			<MenuLinkItemPrimitive
				actionsOnHover={
					shouldShowActionsAndOptions && (
						<ActionsAndOptions
							actions={actions}
							analyticsName={analyticsName}
							optionsMenuElem={optionsMenuElem}
						/>
					)
				}
				dropIndicator={dropIndicator}
				elemBefore={icon}
				elemAfter={!openInSameTab && <LinkExternalIcon label="" size="small" spacing="spacious" />}
				isDragging={isDragging}
				hasDragIndicator
				href={url || ''}
				isSelected={isSelected}
				onClick={onClick}
				ref={mergedRefs}
				target={openInSameTab ? '_self' : '_blank'}
				visualContentRef={dropTargetRef}
			>
				<span aria-label={ariaLabel}>{labelElem ?? label}</span>
			</MenuLinkItemPrimitive>
			{maybeDragPreview}
		</>
	);
}

function isValidUrl(urlString: unknown) {
	if (typeof urlString !== 'string') {
		return false;
	}
	try {
		return Boolean(new URL(urlString));
	} catch {
		return false;
	}
}
